import { useMemo } from 'react';
import { InferType } from 'yup';
import { DayAndHourPickerRowSchema } from '../../../components/common/Form';
import { RestaurantDetailsInterface } from '../../../types/RestaurantInterface';
import { RestaurantHourInterface } from '../../../types/RestaurantHourInterface';
import { fullToShortDayName, shortToFullDayName } from '../../../constants/weekDayConstants';
import { compareMilitaryTime } from '../../../utils/time';
import { RestaurantSocialsSchema, RestaurantWizardSchema, RestaurantWizardFormValues } from './restaurantWizardSchema';

const restaurantHoursToFormik = (hours: RestaurantHourInterface[]): InferType<typeof DayAndHourPickerRowSchema>[] =>
  hours
    .map((block) => {
      const daysOfWeek: any = {};
      Object.entries(fullToShortDayName).forEach(([day]) => {
        daysOfWeek[fullToShortDayName[day]] = block.day.includes(day);
      });
      return {
        id: crypto.randomUUID(),
        daysOfWeek,
        startTime: block.start,
        endTime: block.end
      } as InferType<typeof DayAndHourPickerRowSchema>;
    })
    .sort((a, b) => {
      const [aDay] = Object.entries(a.daysOfWeek).find(([key, value]) => value && shortToFullDayName[key]);
      const [bDay] = Object.entries(b.daysOfWeek).find(([key, value]) => value && shortToFullDayName[key]);
      if (aDay === bDay) {
        return compareMilitaryTime(a.startTime, b.startTime);
      }
      return Object.keys(shortToFullDayName).indexOf(aDay) < Object.keys(shortToFullDayName).indexOf(bDay) ? -1 : 1;
    });

const useRestaurantWizardInitialValues = (
  isEdit: boolean,
  currentRestaurant: RestaurantDetailsInterface | null
): RestaurantWizardFormValues =>
  useMemo((): RestaurantWizardFormValues => {
    if (isEdit) {
      return {
        name: currentRestaurant?.name,
        description: currentRestaurant?.description || '',
        primaryTagline: currentRestaurant?.primaryTagline || '',
        secondaryTagline: currentRestaurant?.secondaryTagline || '',
        phone: currentRestaurant?.phone?.startsWith('+1') ? currentRestaurant.phone : `+1${currentRestaurant?.phone}`,
        email: currentRestaurant?.email,
        cuisine: currentRestaurant?.cuisine?.cuisineID,
        website: currentRestaurant?.website || '',
        layoutID: currentRestaurant?.menuLayout?.layoutID,
        address: {
          restaurantAddressID: currentRestaurant?.address?.restaurantAddressID,
          address1: currentRestaurant?.address?.address1,
          address2: currentRestaurant?.address?.address2 || '',
          city: currentRestaurant?.address?.city,
          governingDistrict: currentRestaurant?.address?.governingDistrict,
          country: currentRestaurant?.address?.country,
          postCode: currentRestaurant?.address?.postalCode
        },
        socials: RestaurantSocialsSchema.cast(currentRestaurant?.socials ?? {}),
        restaurantHours:
          currentRestaurant?.restaurantHours?.length > 0
            ? restaurantHoursToFormik(currentRestaurant?.restaurantHours)
            : [DayAndHourPickerRowSchema.cast({ id: crypto.randomUUID() })],
        availabilityNotes: currentRestaurant?.availabilityNotes ?? ''
      };
    }
    return RestaurantWizardSchema.cast({});
  }, [isEdit, currentRestaurant]);

export default useRestaurantWizardInitialValues;
